import React, { useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { AUTOPILOT_COLORS } from "../autopilot/styles";

// Blur no fundo + destaque no modal (injeção única, self-contained).
if (typeof document !== "undefined" && !document.getElementById("limite-divergencia-modal-styles")) {
  const style = document.createElement("style");
  style.id = "limite-divergencia-modal-styles";
  style.textContent = `
    .modal-backdrop.limite-divergencia-backdrop {
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      background-color: rgba(8, 12, 24, 0.55);
      opacity: 1;
    }
    .modal-content.limite-divergencia-content {
      box-shadow: 0 24px 70px rgba(0, 0, 0, 0.6);
      border: 1px solid var(--everlog-card-border);
      border-radius: 14px;
      overflow: hidden;
    }
  `;
  document.head.appendChild(style);
}

function formatCurrency(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatPercent(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

const temNumero = (v) => v != null && v !== "" && !Number.isNaN(Number(v));

// Card "Limite de divergência" (PO1-2496): mostra o limite efetivo em R$ (clicável).
// Ao clicar, abre um modal read-only explicando a regra (o maior entre o limite
// percentual sobre o frete a receber e o limite absoluto, parâmetros do embarcador).
// Quando o embarcador não tem parâmetros, cai no campo antigo do CT-e (não clicável).
export default function LimiteDivergenciaValue({ summary }) {
  const [open, setOpen] = useState(false);

  if (!summary) return <span>-</span>;

  const pct = summary.limiteDivergenciaPercentualEfetivo; // ex.: 0.5
  const abs = summary.limiteDivergenciaValorAbsoluto; // ex.: 5
  const efetivo = summary.limiteDivergenciaEfetivoReais; // ex.: 95.79
  const freteReceber = summary.valorFreteReceber; // ex.: 19158.28

  if (!temNumero(pct) && !temNumero(abs) && !temNumero(efetivo)) {
    return <span>{formatPercent(summary.limiteDivergenciaPercentual)}</span>;
  }

  const percentualReais =
    temNumero(pct) && temNumero(freteReceber)
      ? Number(freteReceber) * (Number(pct) / 100)
      : null;
  const valorCard = temNumero(efetivo) ? formatCurrency(efetivo) : "-";

  const toggle = (event) => {
    if (event) event.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle(event);
          }
        }}
        title="Ver como o limite é calculado"
        style={{
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.35rem",
          borderBottom: `1px dotted ${AUTOPILOT_COLORS.muted}`,
        }}
      >
        {valorCard}
        <span
          aria-hidden="true"
          style={{
            fontSize: "0.62rem",
            fontWeight: 700,
            lineHeight: 1,
            width: "1.15em",
            height: "1.15em",
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${AUTOPILOT_COLORS.muted}`,
            color: AUTOPILOT_COLORS.muted,
            fontStyle: "italic",
          }}
        >
          i
        </span>
      </span>

      <Modal
        isOpen={open}
        toggle={toggle}
        centered
        fade={false}
        autoFocus={false}
        backdropClassName="limite-divergencia-backdrop"
        contentClassName="limite-divergencia-content"
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "0.8rem",
            padding: "1rem 1.25rem 0.75rem",
            borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
            background: "var(--everlog-card-hero-bg)",
          }}
        >
          <div style={{ fontSize: "1.05rem", fontWeight: 700, color: AUTOPILOT_COLORS.title }}>
            Limite de divergência
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={toggle}
            style={{
              background: "transparent",
              border: "none",
              color: AUTOPILOT_COLORS.muted,
              fontSize: "1.4rem",
              lineHeight: 1,
              cursor: "pointer",
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
        <ModalBody
          style={{ background: AUTOPILOT_COLORS.surface, color: AUTOPILOT_COLORS.text }}
        >
          <p style={{ marginBottom: "1rem", color: AUTOPILOT_COLORS.muted, lineHeight: 1.5 }}>
            O CT-e fica{" "}
            <strong style={{ color: AUTOPILOT_COLORS.text }}>sem divergência</strong> enquanto a
            diferença entre o valor cobrado e o simulado ficar dentro do limite. O limite é o{" "}
            <strong style={{ color: AUTOPILOT_COLORS.text }}>maior</strong> entre os dois parâmetros
            do embarcador:
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: "0.55rem",
              columnGap: "1.25rem",
              marginBottom: "1rem",
            }}
          >
            <span style={{ color: AUTOPILOT_COLORS.muted }}>
              {temNumero(pct) ? `${formatPercent(pct)} do frete a receber` : "Percentual do frete"}
              {temNumero(freteReceber) ? ` (${formatCurrency(freteReceber)})` : ""}
            </span>
            <strong style={{ textAlign: "right" }}>
              {percentualReais != null ? formatCurrency(percentualReais) : "-"}
            </strong>
            <span style={{ color: AUTOPILOT_COLORS.muted }}>Valor fixo</span>
            <strong style={{ textAlign: "right" }}>
              {temNumero(abs) ? formatCurrency(abs) : "-"}
            </strong>
          </div>
          <div
            style={{
              borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
              paddingTop: "0.75rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: "1rem",
            }}
          >
            <span style={{ fontWeight: 600 }}>Aplicado neste CT-e</span>
            <span style={{ fontWeight: 700, fontSize: "1.1rem", color: AUTOPILOT_COLORS.title }}>
              {valorCard}
            </span>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
